import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Clock, HeartHandshake, ShieldCheck, Loader2, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  retentionStart,
  retentionDecline,
  retentionAccept,
  retentionConfirmDeletion,
  retentionRefund,
} from "@/lib/api";

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export const RefundFlow = () => {
  const [email, setEmail] = useState("");
  const [session, setSession] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [busy, setBusy] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [result, setResult] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    if (session && remaining > 0 && !result) {
      timer.current = setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
      return () => clearInterval(timer.current);
    }
  }, [session, remaining, result]);

  const apply = (data) => {
    setSession(data);
    if (typeof data.remaining_seconds === "number") setRemaining(data.remaining_seconds);
  };

  const start = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter the email you purchased with");
      return;
    }
    setBusy(true);
    try {
      apply(await retentionStart(email));
    } catch {
      toast.error("Could not start your return. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const run = async (fn) => {
    setBusy(true);
    try {
      const data = await fn(session.session_id);
      if (data.status === "retained_with_discount" || data.status === "refund_requested") {
        setResult(data);
        clearInterval(timer.current);
      } else {
        apply(data);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const expired = session && remaining <= 0 && !result;

  // ---- Result screen ----
  if (result) {
    return (
      <div className="rounded-3xl bg-[#121118] border border-[var(--neon-cyan)]/30 p-8 text-center" data-testid="refund-result">
        <div className="mx-auto w-14 h-14 rounded-2xl bg-[var(--neon-cyan)]/15 flex items-center justify-center mb-5">
          <Check className="text-[var(--neon-cyan)]" />
        </div>
        <h3 className="font-future text-xl">{result.status === "refund_requested" ? "Refund requested" : "Discount applied"}</h3>
        <p className="mt-3 text-white/60 text-sm leading-relaxed max-w-md mx-auto">{result.message}</p>
        {result.refund_amount != null && (
          <p className="mt-4 font-future text-2xl text-[var(--neon-cyan)]">
            SFr {result.refund_amount.toFixed(2)} <span className="label-caps text-white/40">({result.percent}%)</span>
          </p>
        )}
      </div>
    );
  }

  // ---- Start screen ----
  if (!session) {
    return (
      <div className="rounded-3xl bg-[#121118] border border-white/10 p-8" data-testid="refund-start">
        <div className="flex items-center gap-3 mb-4">
          <HeartHandshake className="text-[var(--neon-magenta)]" size={22} />
          <h3 className="font-future text-lg">Not happy? Request a return</h3>
        </div>
        <p className="text-white/60 text-sm leading-relaxed mb-6">
          We&apos;re sorry it&apos;s not a fit. Enter the email you purchased with and we&apos;ll look after you —
          you may be eligible for a special retention offer.
        </p>
        <form onSubmit={start} className="flex flex-col sm:flex-row gap-2">
          <Input
            data-testid="refund-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Purchase email"
            className="bg-white/5 border-white/15 text-white placeholder:text-white/30 h-12 flex-1"
          />
          <button
            data-testid="refund-start-btn"
            disabled={busy}
            className="label-caps h-12 px-6 rounded-lg bg-white text-black hover:bg-[var(--neon-magenta)] transition-colors duration-300 flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : "Start return"}
          </button>
        </form>
      </div>
    );
  }

  // ---- Active flow ----
  return (
    <div className="rounded-3xl bg-[#121118] border border-white/10 p-8" data-testid="refund-flow">
      {/* countdown */}
      <div className={`flex items-center gap-2 mb-6 label-caps ${remaining <= 60 ? "text-[var(--neon-magenta)]" : "text-white/50"}`} data-testid="refund-timer">
        <Clock size={14} />
        {expired ? "Offer window closed" : <>Limited offer · {fmt(remaining)} left</>}
      </div>

      {expired ? (
        <div data-testid="refund-expired">
          <h3 className="font-future text-lg">This limited-time offer has expired</h3>
          <p className="text-white/60 text-sm mt-3">
            The 5-minute window has closed. For further help, please chat with Aria above or email legal@pivotransition.ch.
          </p>
        </div>
      ) : (
        <AnimatePresence mode="wait">
          {(session.stage === "offer_25" || session.stage === "offer_50" || session.stage === "offer_75") && (
            <motion.div key={session.stage} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <p className="label-caps text-[var(--neon-cyan)] mb-3">Wait — before you go</p>
              <h3 className="font-future text-2xl leading-tight">
                Stay with us and get <span className="text-[var(--neon-cyan)]">{session.current_percent}% back</span>
              </h3>
              <p className="text-white/60 text-sm mt-3">
                We&apos;ll refund {session.current_percent}% of your purchase to your original payment method and you keep full access. That&apos;s
                {session.amount ? ` SFr ${(session.amount * session.current_percent / 100).toFixed(2)}` : " a partial refund"} back in your pocket.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-7">
                <button
                  data-testid="refund-accept-btn"
                  disabled={busy}
                  onClick={() => run(retentionAccept)}
                  className="label-caps px-6 py-3.5 rounded-full bg-[var(--neon-cyan)] text-black hover:bg-white transition-colors duration-300 flex items-center justify-center gap-2"
                >
                  {busy ? <Loader2 size={16} className="animate-spin" /> : `Accept ${session.current_percent}% & stay`}
                </button>
                <button
                  data-testid="refund-decline-btn"
                  disabled={busy}
                  onClick={() => run(retentionDecline)}
                  className="label-caps px-6 py-3.5 rounded-full border border-white/20 text-white/70 hover:text-white hover:border-white transition-colors duration-300"
                >
                  No thanks, continue
                </button>
              </div>
            </motion.div>
          )}

          {session.stage === "deletion_required" && (
            <motion.div key="deletion" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck size={18} className="text-[var(--neon-magenta)]" />
                <p className="label-caps text-white/50">One last step</p>
              </div>
              <h3 className="font-future text-xl leading-tight">Confirm you&apos;ve deleted the documents</h3>
              <p className="text-white/60 text-sm mt-3">
                Because the guides are licensed digital files, a refund requires that you delete every copy you downloaded.
              </p>
              <label className="flex items-start gap-3 mt-6 cursor-pointer">
                <Checkbox checked={deleted} onCheckedChange={setDeleted} data-testid="deletion-checkbox" className="mt-0.5 border-white/30 data-[state=checked]:bg-[var(--neon-cyan)] data-[state=checked]:text-black" />
                <span className="text-sm text-white/75">I confirm I have permanently deleted all documents and copies I received.</span>
              </label>
              <button
                data-testid="confirm-deletion-btn"
                disabled={busy || !deleted}
                onClick={() => run(retentionConfirmDeletion)}
                className="mt-7 label-caps px-6 py-3.5 rounded-full bg-white text-black hover:bg-[var(--neon-magenta)] transition-colors duration-300 disabled:opacity-40 flex items-center gap-2"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : "Confirm & continue"}
              </button>
            </motion.div>
          )}

          {session.stage === "refund_99_offered" && (
            <motion.div key="99" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
              <p className="label-caps text-[var(--neon-magenta)] mb-3">Final offer</p>
              <h3 className="font-future text-2xl leading-tight">
                <span className="text-[var(--neon-cyan)]">99% refund</span> to your original payment method
              </h3>
              <p className="text-white/60 text-sm mt-3">
                {session.amount ? `SFr ${(session.amount * 0.99).toFixed(2)}` : "99% of your purchase"} will be reimbursed to your original
                payment method within <strong className="text-white/80">28 working days</strong>.
              </p>
              <button
                data-testid="request-99-refund-btn"
                disabled={busy}
                onClick={() => run(retentionRefund)}
                className="mt-7 label-caps px-6 py-3.5 rounded-full bg-[var(--neon-cyan)] text-black hover:bg-white transition-colors duration-300 flex items-center gap-2"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : "Request my 99% refund"}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </div>
  );
};
